/**
 * KundeDetailScreen — T2 Kunde entity-detail page.
 * Route: /customers/$id
 * Ported from v0 components/brokly/kunden-view.tsx KundeDetail.
 * V10-J2: header + Policen/Rechnungen/Kommunikation/Beziehungen tabs live-wired
 * (useEntityDetail hooks; mock fallback via VITE_DATA_MODE). Tabs without a real
 * endpoint (Dokumente/Schäden/Aufgaben/Compliance/Historie) + the KI insight
 * cards are mock-mode-only (EMPTY/hidden in live mode).
 * [Source: docs/stories/t2-entity-detail.md#task-3]
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC5]
 */
import { useState } from 'react';
import {
  Building2,
  LayoutDashboard,
  ShieldCheck,
  ReceiptText,
  MessageSquare,
  NetworkIcon,
  FileText,
  History,
  ShieldAlert,
  ListChecks,
  CheckCircle2,
  KeyRound,
  Plus,
  Send,
  ClipboardList,
  Upload,
  Pencil,
  Trash2,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  Coins,
  AlertTriangle,
  Lock,
  Gauge,
  Handshake,
} from 'lucide-react';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { StatusDot } from '../../components/ui/StatusDot';
import { DataTable } from '../../components/data/DataTable';
import { EntityLink } from '../../components/ui/EntityLink';
import { DetailShell } from './components/DetailShell';
import { KpiCard } from './components/KpiCard';
import { InsightCard } from './components/InsightCard';
import { ListPanel } from './components/ListPanel';
import { SparteBadge } from './components/SparteBadge';
import { FaelligText } from './components/FaelligText';
import { Timeline } from './components/Timeline';
import { BeziehungsnetzTab } from './components/struktur/BeziehungsnetzTab';
import { ZugriffFreigabe } from './components/ZugriffFreigabe';
import { FirmenDomains } from './components/FirmenDomains';
import { Hauptansprechperson } from './components/Hauptansprechperson';
import { Zeichnungsberechtigte } from './components/Zeichnungsberechtigte';
import { FirmenStammdaten } from './components/FirmenStammdaten';
import { RisikoprofilTab } from './components/risikoprofil/RisikoprofilTab';
import { AusschreibungenTab } from '../placements/AusschreibungenTab';
import { usePlacementsForKunde } from '../placements/usePlacements';
import { FirmEditDialog } from './components/FirmEditDialog';
import { PolicyRowDeleteAction } from './components/PolicyRowDeleteAction';
import { PolicyRegistrationCell } from './components/PolicyRegistrationCell';
import {
  POLICY_STATUS_META,
  schadenfaelleForKunde,
  aufgabenForKunde,
  complianceForKunde,
  historieForKunde,
} from './mock';
import { ClientDocumentsTab } from './components/ClientDocumentsTab';
import { useClientDocumentRows } from '../documents/hooks/useClientDocumentRows';
import {
  useKundeDetail,
  usePoliciesForKunde,
  useRechnungenForKunde,
  useKommForKunde,
  useBeziehungenForKunde,
  useCustomerStatusOverlay,
  useVorgaengeForKunde,
  useMandateBadges,
  useLiveTermination,
  usePortalStatus,
  mockOnlyList,
} from './hooks/useEntityDetail';
import { KundeVorgangCard, vorgangAnchorId } from './components/KundeVorgangCard';
import { RegisterBefundFlag } from '../mandate/RegisterBefundActions';
import { MandateBadgeTile } from './components/MandateBadgeTile';
import { PortalStatusChip } from './components/PortalStatusChip';
import { RetentionChips } from './components/RetentionChips';
import { refreshMandateQueries } from './mandate-state-queries';
import { MandateTile } from './components/MandateTile';
import { MandateDeliveryActions, MandateDeliveryNotice, mandatePendingAnchorId } from './components/MandateDeliveryActions';
import { qk } from '../../lib/query-keys';
import { kommKategorie } from './api/entity-detail-api';
import { mailTeamMarkerName } from '../communication-v2/mail-visibility';
import { MailTeamBadge } from '../communication-v2/components/MailTeamBadge';
import { isLiveData } from '../../lib/data-mode';
import { ApiError, apiClient } from '../../lib/api-client';
import { UploadPolicyDialog, type PolicyDocKind } from '../policies/components/UploadPolicyDialog';
import { cn } from '../../lib/cn';
import type { Policy, KundeRechnung, Komm, KommKategorie } from './types';
import { chf, dateShortDe } from '../../lib/format';
import { StatusSelect } from '../customers/components/StatusSelect';
import { KUNDE_STATUS_META, MANDATE_TRIGGER_STATUSES, TERMINATION_TRIGGER_STATUSES } from '../customers/customer-status-meta';
import { MandatAufloesenDialog } from '../mandate/termination/MandatAufloesenDialog';
import { TerminationPendingChip } from '../mandate/termination/TerminationPendingChip';
import { formatZurichDateDe, gekuendigtPerLabel, inaktivSeitLabel } from '@brokly/shared';
import { MailTeamSelect } from '../customers/components/MailTeamSelect';
import { useCustomerStatusMutation } from '../customers/hooks/useCustomerStatusMutation';
import { useCustomerMailTeamMutation } from '../customers/hooks/useCustomerMailTeamMutation';
import { useDeleteCustomer } from '../customers/hooks/useDeleteCustomer';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useMailVisibilityTeams } from '../administration/hooks';
import { useEffectiveVisibility } from '../administration/useEffectiveVisibility';
import { Dialog } from '../administration/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MandateWizardDialog } from '../mandate/MandateWizardDialog';
import { MandateRegistrationPanel } from '../mandate/MandateRegistrationPanel';
import { MandateEintragungenTab } from '../mandate/MandateEintragungenTab';
import { useRegistrationTile } from '../mandate/mandate-registration-view-queries';
import { RegistrationTile } from './components/RegistrationTile';
import { VISIBILITY_KEY_KUNDEN_MANDATE_EINTRAGUNGEN } from '@brokly/shared';
import { VersichererBenachrichtigenDialog } from '../mandate/VersichererBenachrichtigenDialog';
import { VersichererBenachrichtigungenPanel } from '../mandate/VersichererBenachrichtigungenPanel';
import { useVersichererBenachrichtigungMandate } from '../mandate/versicherer-benachrichtigung-queries';
import type { KundeStatus } from '../entity-detail/types';


/**
 * Stefan's 4 Kommunikation buckets for the client-detail tab filter, plus an "Alle" default.
 * The list is a flat feed (all buckets merged, newest first); this segmented control filters it.
 */
const KOMM_BUCKETS: { value: 'alle' | KommKategorie; label: string }[] = [
  { value: 'alle', label: 'Alle' },
  { value: 'eingang', label: 'Eingang' },
  { value: 'ausgang', label: 'Ausgang' },
  { value: 'gesendet', label: 'Gesendet' },
  { value: 'freigabe', label: 'Freigabe' },
];

const KOMM_KATEGORIE_LABEL: Record<KommKategorie, string> = {
  eingang: 'Eingang',
  ausgang: 'Ausgang',
  gesendet: 'Gesendet',
  freigabe: 'Freigabe',
};

/**
 * Bereich 3 (section/card visibility): the Kunde-detail tab `value` → visibility
 * catalog TAB key. `struktur` is the shipped tab value for the catalog's
 * `kunden.beziehungen` tab (naming predates the catalog). Tabs with no catalog
 * key (schaeden/aufgaben/zugriff/compliance are stub/overflow) are never hidden
 * by the visibility layer → undefined = always shown.
 */
const KUNDE_TAB_VISIBILITY_KEY: Record<string, string | undefined> = {
  uebersicht: 'kunden.uebersicht',
  policen: 'kunden.policen',
  rechnungen: 'kunden.rechnungen',
  kommunikation: 'kunden.kommunikation',
  struktur: 'kunden.beziehungen',
  dokumente: 'kunden.dokumente',
  aktivitaet: 'kunden.aktivitaet',
  risikoprofil: 'kunden.risikoprofil',
  ausschreibungen: 'kunden.ausschreibungen',
  mandate_eintragungen: VISIBILITY_KEY_KUNDEN_MANDATE_EINTRAGUNGEN,
};

export function KundeDetailScreen() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const navigate = useNavigate();
  const live = isLiveData();

  // Server state (V10-J2) — all hooks BEFORE any early return.
  const { data: detail, isLoading } = useKundeDetail(id);
  const kunde = detail?.kunde;
  const subjectType = detail?.subjectType;
  // Bereich 3: the tab-AND-card visibility layer. `isHidden(key)` REMOVES a tab or
  // card from the client-detail UI for a role whose effective visibility denies it
  // (ancestor-aware: a denied tab hides its cards too). Empty in mock mode.
  // Resolved BEFORE the per-tab data hooks so a denied tab can also SKIP its fetch (E5).
  const { isHidden } = useEffectiveVisibility();
  // E5: a denied tab must not even FETCH its data (DOM removal alone still shipped the
  // payload to the client + React Query cache). Passing `undefined` subjectType trips
  // each hook's existing `enabled: Boolean(subjectType)` gate in live mode → no request.
  // (These feeds are used ONLY by their own tab, never by the always-visible Übersicht.)
  const kommSubjectType = isHidden('kunden.kommunikation') ? undefined : subjectType;
  const beziehungenSubjectType = isHidden('kunden.beziehungen') ? undefined : subjectType;
  // P2 Placement Slice 4: the Ausschreibungen tab count — E5-gated (a denied tab must not fetch).
  const ausschreibungenSubjectType = isHidden('kunden.ausschreibungen') ? undefined : subjectType;
  const { data: policen = [] } = usePoliciesForKunde(id, kunde?.name ?? '');
  const { data: rechnungen = [] } = useRechnungenForKunde(id, subjectType);
  const { data: komm = [] } = useKommForKunde(id, kommSubjectType);
  const { data: beziehungen = [] } = useBeziehungenForKunde(id, beziehungenSubjectType);
  // P2 Placement Slice 4: Placements for the Ausschreibungen tab badge (the tab dedupes this key).
  const { data: ausschreibungenData } = usePlacementsForKunde(id, ausschreibungenSubjectType);
  // #6: the "(in Verarbeitung)" overlay + the mandate Vorgang lifecycle, surfaced on the
  // Übersicht. Both are live-only reads; undefined/[] in mock mode (nothing extra renders).
  const { data: statusOverlay } = useCustomerStatusOverlay(id, subjectType);
  const { data: vorgaenge = [] } = useVorgaengeForKunde(id, subjectType);
  const { data: mandateBadges } = useMandateBadges(id, subjectType);
  const { data: liveTermination } = useLiveTermination(id, subjectType);
  const { data: portalStatus } = usePortalStatus(id, subjectType);
  // The area «Mandate & Eintragungen» (Brokermandat Kap. 7.7) and its header tile «Eintragung bei
  // Versicherern» (10.5): a denied tab neither shows nor fetches its tile (E5). Live-only.
  const eintragungenHidden = isHidden(VISIBILITY_KEY_KUNDEN_MANDATE_EINTRAGUNGEN);
  const { data: registrationTile } = useRegistrationTile(subjectType, id, { enabled: live && !eintragungenHidden });
  // P4-S7: the customer's Analysemandat as the Versicherer-Benachrichtigung sees it. Its mandate id
  // keys the notification panel, so the panel shows whenever the customer has an Analysemandat, not
  // only right after a dispatch from this screen. A light read: the dialog loads its own data when opened.
  const meldungHidden = isHidden('kunden.uebersicht.meldung');
  const vbMandate = useVersichererBenachrichtigungMandate(subjectType ?? 'firma', id, {
    enabled: live && subjectType !== undefined && !meldungHidden,
  });
  // DMS M12: the Dokumente tab (dms/spec.md §4.2) — active/latest-version-only
  // documents for this client. Called here too (not just inside
  // ClientDocumentsTab) so the tab badge count is available; TanStack Query
  // dedupes the identical `qk.clientDocuments(id)` key, so this is NOT a
  // second network request.
  const { rows: clientDocuments } = useClientDocumentRows(id);

  // E1: the status write. The api guard is the hard line; this only decides
  // whether to offer the control or the plain badge.
  const statusMutation = useCustomerStatusMutation();
  const { can: canKey } = useEffectivePermissions();
  // P6 (7.13): an inaktiv customer is write-protected. The api refuses every write (409 customer_read_only);
  // the screen offers none: every write flag below is off, lists and downloads stay.
  const readOnly = statusOverlay?.readOnly === true || statusOverlay?.status === 'inaktiv';
  const can = (key: string): boolean => canKey(key) && !readOnly;
  const canTransition = can('customer_status.transition');
  const canOverride = can('customer_status.override');
  // The delivery of a released post/portal mandate: post and the withdrawal need mandate.dispatch,
  // the portal Zustellen mails the notification and needs communication.send as well.
  const canDispatchMandate = can('mandate.dispatch');
  const canSendMandate = can('communication.send');
  // MV: the write needs `visibility.manage`, the team LIST needs `admin.team.read`. Without
  // the list `teams` is undefined and the control falls back to the name on the wire.
  const mailTeamMutation = useCustomerMailTeamMutation();
  const canManageVisibility = can('visibility.manage');
  const kundenteamsQuery = useMailVisibilityTeams();
  // D2: the api is the hard line; this only decides whether to offer the editor.
  const canWriteCrm = can('crm.write');
  // P2 Placement Slice 1: the Risikoprofil edit controls. The api guard is the hard line.
  const canWriteRisk = can('customer_risk.write');
  // P2 Placement Slice 4: the «Neues Placement» control. The api guard is the hard line.
  const canWritePlacement = can('placement.write');

  // P2 Placement Slice 4: seed the active tab from ?tab= (deep-links + «Zurück» from an Ausschreibung).
  const search = useSearch({ strict: false }) as { tab?: string };
  const [activeTab, setActiveTab] = useState(search.tab ?? 'uebersicht');
  /** Kommunikation tab: which of Stefan's 4 buckets to show ('alle' = the merged feed). */
  const [kommFilter, setKommFilter] = useState<'alle' | KommKategorie>('alle');
  /**
   * E6/E2: the status the broker just picked, while the mandate wizard is open.
   *
   * `provisorisch` and `aktiv` do not write a status directly. They open the wizard, and
   * the api writes the status only when the mail actually goes out. So this is a purely
   * optimistic value: on cancel it is simply dropped, which is what makes E2's "cancel =
   * roll back to the original status" free of any compensating write - and therefore
   * unable to clear an "in Verarbeitung" overlay it should have restored (E-9).
   */
  const [wizardTarget, setWizardTarget] = useState<KundeStatus | null>(null);
  /** P6: the dialog «Mandat auflösen», opened by the status pick «Gekündigt». */
  const [terminationOpen, setTerminationOpen] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);
  /** A pending-delivery action's notice (the withdraw 409), kept here so it outlives the tile. */
  const [mandateNotice, setMandateNotice] = useState<string | null>(null);
  /**
   * J: the mandate the wizard just dispatched, so the explicit insurer-notification workflow
   * (trigger + proof panel) has a real mandate id to act on. There is no persisted
   * mandate-by-customer read in the web yet, so this is the surface where a mandate id becomes
   * available - the section shows once a mandate has been sent from this screen.
   */
  const [mandateId, setMandateId] = useState<string | null>(null);
  // P4-S7: remember the mandate kind so an Analysemandat shows the «Versicherer benachrichtigen»
  // panel rather than the legacy J1 blast (the broker path is unchanged, A3).
  const [mandateKind, setMandateKind] = useState<string | null>(null);
  // P4-S7: the «Versicherer benachrichtigen» dialog, offered to a caller holding communication.send
  // (the send's permission; the api is the hard line). The mandate its send returns keys the panel.
  const canSendInsurers = can('communication.send');
  const [vbOpen, setVbOpen] = useState(false);
  const [vbSentMandateId, setVbSentMandateId] = useState<string | null>(null);
  const vbMandateId =
    vbSentMandateId ?? vbMandate.data?.mandate_id ?? (mandateKind === 'analyse' ? mandateId : null);
  // The signed Brokermandat (its tile reads «unterzeichnet», or «gekündigt per …» while it runs out:
  // the registrations stay to be worked until the Enddatum, P6): its registrations with the insurers.
  const brokerMandateId =
    mandateBadges?.broker.state === 'unterzeichnet' || mandateBadges?.broker.state === 'gekuendigt'
      ? mandateBadges.broker.mandateId
      : null;
  /**
   * K4: the soft-delete confirm. `Löschen` (firma only) opens this; confirming calls
   * DELETE /customers/:id, then navigates back to the Kunden list. The api guard
   * (crm.write) stays the hard line; canWriteCrm only decides whether to offer it.
   */
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteMutation = useDeleteCustomer();
  /**
   * An unconfirmed record's page offers the Kunden view's two decisions. The api owns the outcome
   * (409 when the record is no longer open); the ['customers'] prefix also covers this page's own
   * record, which comes from the same list.
   */
  const refreshAfterDecision = () => {
    void qc.invalidateQueries({ queryKey: ['customers'] });
    void qc.invalidateQueries({ queryKey: ['posteingang'] });
  };
  const confirmMutation = useMutation<void, Error, string>({
    mutationFn: (subjectId) => apiClient.customers.confirmCustomer(subjectId),
    onSuccess: () => {
      showFeedback('Als Kunde übernommen.');
      refreshAfterDecision();
    },
    onError: (err) =>
      showFeedback(err instanceof ApiError ? err.message : 'Kunde konnte nicht übernommen werden.'),
  });
  const declineMutation = useMutation<void, Error, string>({
    mutationFn: (subjectId) => apiClient.customers.declineCustomer(subjectId),
    onSuccess: () => {
      showFeedback('Als «Kein Kunde» markiert.');
      refreshAfterDecision();
    },
    onError: (err) =>
      showFeedback(
        err instanceof ApiError ? err.message : 'Konnte nicht als «Kein Kunde» markiert werden.',
      ),
  });
  /**
   * D1-14: the "Bearbeiten" edit dialog (firma only). `firmRefreshKey` is bumped after any edit
   * (from the dialog OR the inline card) so the Firmenstammdaten card refetches the persisted
   * values, and the header/name is invalidated so a rename shows immediately.
   */
  const [firmEditOpen, setFirmEditOpen] = useState(false);
  const [firmRefreshKey, setFirmRefreshKey] = useState(0);
  /** docs-fix item 1: the "Neue Police" upload dialog (holder prefilled to this Kunde). */
  const [uploadPoliceOpen, setUploadPoliceOpen] = useState(false);

  async function handleUploadPolicy(file: File, opts: { holderId: string; docKind: PolicyDocKind }) {
    if (!live) {
      showFeedback('Police wird hochgeladen und analysiert …');
      return { document_id: 'mock-doc', job_id: 'mock-job', status: 'queued' };
    }
    const res = await apiClient.policies.upload(file, opts);
    // The Police is created asynchronously (extraction queue) — refresh this Kunde's Policen
    // tab + the Ablage so it appears once the worker finishes; confirm via the feedback toast.
    void qc.invalidateQueries({ queryKey: ['policies'] });
    void qc.invalidateQueries({ queryKey: ['documents'] });
    showFeedback('Police wird hochgeladen und analysiert, sie erscheint gleich unter Policen.');
    return res;
  }

  function onFirmSaved() {
    setFirmRefreshKey((k) => k + 1);
    void qc.invalidateQueries({ queryKey: ['kunde', id] });
    void qc.invalidateQueries({ queryKey: ['customers'] });
  }

  function showFeedback(msg: string) {
    setFeedbackMsg(msg);
    setTimeout(() => setFeedbackMsg(null), 2000);
  }

  // A mandate moved (sent, delivered or its release withdrawn): refresh the customer, the overlay,
  // the Vorgang board and the Mandatskacheln immediately rather than on the next refocus. The
  // Versicherer-Benachrichtigung prefix too: its dialog and panel key on the live Analysemandat,
  // a released one included, so every one of these moves can change them.
  function refreshMandateState() {
    refreshMandateQueries(qc, id, subjectType);
  }

  // A pending-delivery action moved the mandate. Its notice (a withdraw that lost to a delivery) is
  // shown here: the refetch removes the pending tile, and with it the actions' own dialog.
  function onMandateDeliveryChanged(notice?: string) {
    refreshMandateState();
    if (notice) setMandateNotice(notice);
  }

  // The released post/portal mandates waiting for their delivery (one per kind at most).
  const pendingDeliveries = mandateBadges
    ? [mandateBadges.analyse, mandateBadges.broker].flatMap((b) =>
        b.pendingDelivery ? [{ kind: b.kind, delivery: b.pendingDelivery }] : [],
      )
    : [];

  // The wizard's «Offenes Mandat anzeigen»: open the Übersicht at the blocking mandate, its
  // pending-delivery tile when it waits for Zustellen, else its Vorgang row.
  function showMandate(mandateId: string) {
    setActiveTab('uebersicht');
    const badge = mandateBadges
      ? [mandateBadges.analyse, mandateBadges.broker].find((b) => b.mandateId === mandateId || b.pendingDelivery?.mandateId === mandateId)
      : undefined;
    const vorgangId = badge?.vorgangId ?? vorgaenge.find((v) => v.mandateId === mandateId)?.vorgangId ?? null;
    const anchor = vorgangId !== null ? vorgangAnchorId(vorgangId) : mandatePendingAnchorId(mandateId);
    const scroll = () => {
      const el = typeof document !== 'undefined' ? document.getElementById(anchor) : null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return el !== null;
    };
    if (!scroll() && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => void scroll());
    }
  }

  if (!kunde) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <p className="text-sm text-muted-foreground">
          {isLoading ? 'Lade Kunde …' : 'Kunde nicht gefunden.'}
        </p>
        {!isLoading && (
          <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/customers' })}>
            Zurück zu Kunden
          </Button>
        )}
      </div>
    );
  }

  // Mock-only tabs (no real endpoint yet) — EMPTY in live mode (V10-J2).
  // NOTE: Dokumente is no longer in this bucket — DMS M12 gave it a real
  // endpoint (GET /documents?client_id=), wired above via useClientDocumentRows.
  const schaeden = mockOnlyList(() => schadenfaelleForKunde(kunde.id));
  const aufgaben = mockOnlyList(() => aufgabenForKunde(kunde.id));
  const compliance = mockOnlyList(() => complianceForKunde(kunde.id));
  const historie = mockOnlyList(() => historieForKunde(kunde.id));

  const offeneRechnungen = rechnungen.filter((r) => r.status.label !== 'Bezahlt').length;
  const naechstesRenewal = policen
    .filter((p) => p.renewalTage >= 0)
    .reduce<
      number | null
    >((min, p) => (min === null ? p.renewalTage : Math.min(min, p.renewalTage)), null);

  // ── Columns ────────────────────────────────────────────────────────────────

  const policyColumns: ColumnDef<Policy>[] = [
    {
      accessorKey: 'nr',
      header: 'Policen-Nr.',
      // Clickability rule (dms/spec.md §4.6): in the client Policen tab, rows are
      // NOT clickable as a whole — ONLY Policen-Nr. navigates to the policy detail.
      cell: ({ row }) => (
        <EntityLink
          onActivate={() =>
            void navigate({ to: '/policen/$id', params: { id: row.original.id } })
          }
          className="mono-num text-xs font-medium"
        >
          {row.original.nr}
        </EntityLink>
      ),
    },
    {
      accessorKey: 'versicherer',
      header: 'Versicherer',
      // §4.6: plain text — was an EntityLink to /versicherer/:id; removed on
      // purpose so Policen-Nr. is the row's only navigating cell.
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.versicherer}</span>
      ),
    },
    {
      accessorKey: 'sparte',
      header: 'Sparte',
      cell: ({ row }) => <SparteBadge sparte={row.original.sparte} />,
    },
    {
      accessorKey: 'praemie',
      header: 'Prämie',
      cell: ({ row }) => (
        <div className="text-right mono-num">
          {row.original.praemie === null ? '-' : chf(row.original.praemie)}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const m = POLICY_STATUS_META[row.original.status];
        return <StatusDot tone={m.tone} label={m.label} />;
      },
    },
    {
      // P5-S5: the Brokermandat registration (Eingetragen, Ausgenommen, ...) + the per-policy exception.
      id: 'eintragung',
      header: 'Eintragung',
      cell: ({ row }) => <PolicyRegistrationCell policy={row.original} readOnly={readOnly} />,
    },
    {
      accessorKey: 'renewalTage',
      header: 'Renewal',
      cell: ({ row }) => <FaelligText restTage={row.original.renewalTage} showIcon={false} />,
    },
    {
      id: 'aktionen',
      header: '',
      // DMS Part2 P2-13 (§1.3): a trash control, visible ONLY when the row's
      // CURRENT document is inactive AND the viewer holds `document.delete` —
      // rows not clickable, Police-Nr. stays the only nav cell (§4.6 unchanged).
      cell: ({ row }) => (
        <div className="flex justify-end">
          <PolicyRowDeleteAction policy={row.original} readOnly={readOnly} />
        </div>
      ),
    },
  ];

  const rechnungColumns: ColumnDef<KundeRechnung>[] = [
    {
      accessorKey: 'nr',
      header: 'Rechnungs-Nr.',
      cell: ({ row }) => <span className="mono-num text-xs">{row.original.nr}</span>,
    },
    {
      accessorKey: 'datum',
      header: 'Datum',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.datum}</span>,
    },
    {
      accessorKey: 'betrag',
      header: 'Betrag',
      cell: ({ row }) => <div className="text-right mono-num">{chf(row.original.betrag)}</div>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusDot tone={row.original.status.tone} label={row.original.status.label} />
      ),
    },
  ];

  // ── Tabs config ────────────────────────────────────────────────────────────

  const allTabs = [
    { value: 'uebersicht', label: 'Übersicht', icon: LayoutDashboard },
    { value: 'policen', label: 'Policen', icon: ShieldCheck, count: policen.length },
    { value: 'mandate_eintragungen', label: 'Mandate & Eintragungen', icon: Handshake },
    { value: 'risikoprofil', label: 'Risikoprofil', icon: Gauge },
    { value: 'ausschreibungen', label: 'Ausschreibungen', icon: ClipboardList, count: ausschreibungenData?.items.length },
    { value: 'rechnungen', label: 'Rechnungen', icon: ReceiptText, count: rechnungen.length },
    { value: 'kommunikation', label: 'Kommunikation', icon: MessageSquare, count: komm.length },
    { value: 'struktur', label: 'Struktur', icon: NetworkIcon, count: beziehungen.length },
    { value: 'dokumente', label: 'Dokumente', icon: FileText, count: clientDocuments.length },
    { value: 'aktivitaet', label: 'Aktivität', icon: History },
    { value: 'schaeden', label: 'Schäden', icon: ShieldAlert, count: schaeden.length },
    { value: 'aufgaben', label: 'Aufgaben', icon: ListChecks, count: aufgaben.length },
    { value: 'zugriff', label: 'Zugriff', icon: KeyRound },
  ];
  // Bereich 3 (tab-level): drop tabs whose catalog key is denied for this role.
  const tabs = allTabs.filter((t) => {
    const key = KUNDE_TAB_VISIBILITY_KEY[t.value];
    return key === undefined || !isHidden(key);
  });
  // When the current tab was hidden (e.g. the default `uebersicht` is denied), fall
  // back to the first still-visible tab so the shell never renders a hidden body.
  const shownTab = tabs.some((t) => t.value === activeTab) ? activeTab : tabs[0]?.value ?? activeTab;

  // E4: the Kopfbereich Jahresprämie number + Risiko badge were rendered UNGATED — a role
  // that denies the Jahresprämie/Kennzahlen (or Risiko) still saw them in the header
  // ("toggle without effect"). Gate them with their deny keys so the header respects the
  // same visibility contract as the cards below (jahrespraemie mirrors the Kennzahlen row,
  // which also shows the annual premium).
  const headerJahrespraemieHidden =
    isHidden('kunden.uebersicht.jahrespraemie') || isHidden('kunden.uebersicht.kennzahlen');
  const headerRisikoHidden = isHidden('kunden.risiko');

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-0 p-4">
      {/* Feedback banner */}
      {feedbackMsg && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-sem-ok/40 bg-sem-ok-bg px-4 py-2.5 text-sm text-sem-ok shadow-md"
        >
          {feedbackMsg}
        </div>
      )}

      <MandateDeliveryNotice message={mandateNotice} onDismiss={() => setMandateNotice(null)} />

      <DetailShell
        icon={Building2}
        iconClassName="bg-foreground text-background"
        title={kunde.name}
        // P4 7.6: the Kundennummer sits in the header next to the name (rendered inline after the
        // title, outside the heading element). Live reads it from the status overlay; the list
        // row's kundennummer is the mock-mode / first-paint fallback.
        status={
          (statusOverlay?.customerNumber ?? kunde.kundennummer) ? (
            <span className="mono-num text-sm font-normal text-muted-foreground">
              {statusOverlay?.customerNumber ?? kunde.kundennummer}
            </span>
          ) : undefined
        }
        extraBadge={
          (kunde.risiko && !headerRisikoHidden) || kunde.isUnconfirmed ? (
            <>
              {kunde.risiko && !headerRisikoHidden && (
                <Badge variant="alert">
                  <AlertTriangle className="size-3" />
                  Risiko
                </Badge>
              )}
              {kunde.isUnconfirmed && <Badge variant="neutral">Unbestätigt</Badge>}
            </>
          ) : undefined
        }
        // E1 + E4: status top right and editable, with the date the current
        // status started under it. E4 forbids the creation date and the word
        // "Kunde", so the meta line lost "Kunde seit <Jahr>".
        statusBlock={
          <div className="flex flex-col items-end gap-1">
            {kunde.isUnconfirmed ? (
              // While the record is unconfirmed the two buttons are the only way to sort it.
              <StatusDot
                tone={KUNDE_STATUS_META[kunde.status].tone}
                label={KUNDE_STATUS_META[kunde.status].label}
              />
            ) : (
              <StatusSelect
                status={kunde.status}
                allowedTransitions={kunde.allowedTransitions}
                canTransition={canTransition}
                canOverride={canOverride}
                onChange={async (next) => {
                  // P6: «Gekündigt» opens the dialog «Mandat auflösen» instead of writing (the api
                  // answers 409 termination_required for a manual move; the Freigabe writes it).
                  if (TERMINATION_TRIGGER_STATUSES.includes(next)) {
                    setTerminationOpen(true);
                    return;
                  }
                  // E6: a mandate-generating status opens the wizard instead of writing.
                  // E2: no confirmation in front of it - the pick IS the trigger.
                  if (MANDATE_TRIGGER_STATUSES.includes(next)) {
                    setWizardTarget(next);
                    return;
                  }
                  await statusMutation.mutateAsync({
                    subjectType: kunde.subjectType,
                    subjectId: kunde.id,
                    toStatus: next,
                    mode: kunde.allowedTransitions.includes(next) ? 'transition' : 'override',
                  });
                }}
              />
            )}
            {/* P6 (7.14): the chip «gekündigt per <Enddatum>» of a gekündigt customer. API date. Live-only. */}
            {statusOverlay?.status === 'gekuendigt' && (
              <Badge variant="alert" data-testid="gekuendigt-per-chip">
                {gekuendigtPerLabel(statusOverlay.endDate)}
              </Badge>
            )}
            {/* P6 (7.14): the chip «inaktiv seit <Zurich day of the run>». API date. Live-only. */}
            {statusOverlay?.status === 'inaktiv' && (
              <Badge variant="neutral" data-testid="inaktiv-seit-chip">
                {inaktivSeitLabel(statusOverlay.inactivatedAt ?? null)}
              </Badge>
            )}
            {/* P6-S7 (7.14, Kap. 8): «Aufbewahrung bis …», «Legal Hold» and the hold dialog (tenant.manage).
                The hold is a retention act on an inaktiv customer, so it is offered there too. Live-only. */}
            {statusOverlay?.retention && (
              <RetentionChips
                subjectType={kunde.subjectType}
                subjectId={kunde.id}
                status={statusOverlay.status}
                retention={statusOverlay.retention}
                canManage={canKey('tenant.manage')}
              />
            )}
            {/* P6 (UC1/UC3): a Kündigung is open and waits for its Freigabe (or for the Berechtigungsprüfung);
                the chip links to the task (the Prüfaufgabe while in Prüfung, else the Freigabe task). Live-only. */}
            {liveTermination && (liveTermination.status === 'eroeffnet' || liveTermination.status === 'in_pruefung') && (
              <TerminationPendingChip
                taskId={liveTermination.status === 'in_pruefung' ? (liveTermination.checkTaskId ?? null) : (liveTermination.freigabeTaskId ?? null)}
              />
            )}
            {/* P4-S2 (7.6/10.5): the Mandatskachel. For mandate Vorgaenge it REPLACES the bare
                "in Verarbeitung" badge (no double signal) and carries Mahnstufe + Zustellzeitpunkt +
                versendender User; a click scrolls to the Vorgang row, since the web has no
                standalone Versandkontrolle board route yet. Live-only. */}
            {live && vorgaenge.some((v) => v.docKind === 'mandat') && (
              <MandateTile
                vorgaenge={vorgaenge}
                live={live}
                onOpenVorgang={(vorgangId) => {
                  const el =
                    typeof document !== 'undefined' ? document.getElementById(`vorgang-${vorgangId}`) : null;
                  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              />
            )}
            {/* #6: the "(in Verarbeitung)" overlay for a NON-mandate overlay (no mandate Vorgang row
                present). A mandate Vorgang shows the MandateTile above instead (no double signal). */}
            {statusOverlay?.inVerarbeitung && !vorgaenge.some((v) => v.docKind === 'mandat') && (
              <Badge variant="warning" className="gap-1">
                <RefreshCw className="size-3 animate-spin [animation-duration:3s]" />
                in Verarbeitung
              </Badge>
            )}
            {/* P4-S1 (7.6): the two Mandatskacheln (Analysemandat, Brokermandat). API-computed
                state + label; a click scrolls to the Vorgang row in the Uebersicht (O10). Live-only. */}
            {mandateBadges && (
              <div className="flex flex-wrap items-center gap-1" data-testid="mandate-badges">
                {/* A released post/portal mandate waits for its delivery: its tile carries
                    Zustellen and Freigabe zurückziehen instead of the bare "nicht erteilt". */}
                {[mandateBadges.analyse, mandateBadges.broker].map((badge) =>
                  badge.pendingDelivery ? (
                    <MandateDeliveryActions
                      key={badge.kind}
                      layout="tile"
                      kind={badge.kind}
                      pending={badge.pendingDelivery}
                      canDispatch={canDispatchMandate}
                      canSend={canSendMandate}
                      onChanged={onMandateDeliveryChanged}
                    />
                  ) : (
                    <MandateBadgeTile key={badge.kind} badge={badge} onSelectTab={setActiveTab} />
                  ),
                )}
              </div>
            )}
            {/* (10.5): the tile «Eintragung bei Versicherern» («x von n eingetragen» + chips), a click opens
                the area. Shown once the customer has a Brokermandat. API-computed. Live-only. */}
            {registrationTile && (registrationTile.tiles.total > 0 || registrationTile.mandates.broker !== null) && (
              <RegistrationTile tiles={registrationTile.tiles} onOpen={() => setActiveTab('mandate_eintragungen')} />
            )}
            {/* (10.5): the Portalstatus next to the Mandatskacheln. API-computed. Live-only. */}
            {portalStatus && <PortalStatusChip status={portalStatus} />}
            {/* Brokermandat: the red flag of an uncleared Register-Befund (the lock on this customer),
                with «Freigeben» for tenant.manage. Live-only. */}
            {live && (
              <RegisterBefundFlag subjectType={kunde.subjectType} subjectId={kunde.id} readOnly={readOnly} />
            )}
            {kunde.statusSince !== null && (
              <span className="text-xs text-muted-foreground">
                seit {dateShortDe(kunde.statusSince)}
              </span>
            )}
            {/* MV: which team may see THIS client's mail. Never restricts the record. */}
            <MailTeamSelect
              mailTeamId={kunde.mailTeamId ?? null}
              mailTeamName={kunde.mailTeamName ?? null}
              canManage={canManageVisibility}
              teams={kundenteamsQuery.data?.teams.map((t) => ({ id: t.id, name: t.name }))}
              onChange={async (next) => {
                await mailTeamMutation.mutateAsync({
                  subjectType: kunde.subjectType,
                  subjectId: kunde.id,
                  teamId: next,
                });
              }}
            />
          </div>
        }
        // G1-1: the subtitle is "Ort · Kanton" (e.g. "Sursee · LU"), NOT "Ort · Ort".
        // dedup guards against the two segments being identical (never render "Sursee · Sursee").
        meta={[kunde.ort, kunde.kanton]
          .map((s) => s?.trim())
          .filter(Boolean)
          .filter((v, i, a) => a.indexOf(v) === i)
          .join(' · ')}
        keyNumberLabel={headerJahrespraemieHidden ? undefined : 'Jahresprämie'}
        keyNumber={headerJahrespraemieHidden ? undefined : chf(kunde.jahrespraemie)}
        primaryAction={
          <>
            {kunde.isUnconfirmed && canWriteCrm && (
              <>
                <Button
                  size="sm"
                  disabled={confirmMutation.isPending || declineMutation.isPending}
                  onClick={() => confirmMutation.mutate(kunde.id)}
                >
                  Als Kunde übernehmen
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={confirmMutation.isPending || declineMutation.isPending}
                  onClick={() => declineMutation.mutate(kunde.id)}
                >
                  Kein Kunde
                </Button>
              </>
            )}
            {live && canSendInsurers && !meldungHidden && (
              <Button size="sm" variant="outline" onClick={() => setVbOpen(true)} data-testid="kunde-vb-open">
                <Send className="size-3.5" />
                Versicherer benachrichtigen
              </Button>
            )}
            {!readOnly && (
              <Button size="sm" onClick={() => setUploadPoliceOpen(true)}>
                <Plus className="size-3.5" />
                Neue Police
              </Button>
            )}
          </>
        }
        actions={readOnly ? [] : [
          { label: 'Nachricht senden', icon: Send, onClick: () => showFeedback('Nachricht …') },
          {
            label: 'Aufgabe erstellen',
            icon: ClipboardList,
            onClick: () => showFeedback('Aufgabe …'),
          },
          { label: 'Dokument hochladen', icon: Upload, onClick: () => showFeedback('Upload …') },
          {
            label: 'Bearbeiten',
            icon: Pencil,
            // D1-14: real Firmenstammdaten edit for a firma the user may edit; stub otherwise
            // (person master-data edit is out of scope, and the api rejects without crm.write).
            onClick:
              subjectType === 'firma' && canWriteCrm
                ? () => setFirmEditOpen(true)
                : () => showFeedback('Bearbeiten …'),
            separator: true,
          },
          {
            label: 'Löschen',
            icon: Trash2,
            // K4: real soft-delete for a firma the user may edit; stub otherwise (person-delete
            // is out of MVP scope, and the api rejects a delete without crm.write anyway).
            onClick:
              subjectType === 'firma' && canWriteCrm
                ? () => setDeleteOpen(true)
                : () => showFeedback('Löschen …'),
            destructive: true,
            separator: true,
          },
        ]}
        backTo={{ label: 'Kunden', onClick: () => void navigate({ to: '/customers' }) }}
        tabs={tabs}
        primaryCount={9}
        activeTab={shownTab}
        onTabChange={setActiveTab}
      >
        {/* P6 (7.13): the read-only notice of an inaktiv customer, on every tab. */}
        {readOnly && (
          <div role="status" className="mb-4 rounded-lg border bg-muted/40 p-3 text-sm" data-testid="inaktiv-banner">
            {(() => {
              const day = statusOverlay?.inactivatedAt ? formatZurichDateDe(statusOverlay.inactivatedAt) : null;
              return `Inaktiver Kunde, schreibgeschützt${day ? ` seit ${day}` : ''}. Dokumente können weiterhin heruntergeladen werden.`;
            })()}
          </div>
        )}
        {/* Übersicht */}
        {shownTab === 'uebersicht' && (
          <div className="flex flex-col gap-5">
            {/* KI insight cards: mock-mode only until the extraction/insight surfaces exist (V10-J2).
                Bereich 3: removed when the role hides the `kunden.uebersicht.hinweise` Kachel. */}
            {!live && !isHidden('kunden.uebersicht.hinweise') && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <InsightCard
                  title="KI-Empfehlung · Renewal"
                  konfidenz={82}
                  actions={[
                    {
                      action: 'renewal',
                      label: 'Renewal starten',
                      icon: RefreshCw,
                      variant: 'outline',
                    },
                    {
                      action: 'aufgabe',
                      label: 'Aufgabe erstellen',
                      icon: ClipboardList,
                      variant: 'ghost',
                    },
                    {
                      action: 'ignorieren',
                      label: 'Ignorieren',
                      icon: AlertTriangle,
                      variant: 'ghost',
                      pushEnd: true,
                    },
                  ]}
                  onAction={(a) => {
                    if (a === 'renewal') showFeedback('Renewal gestartet');
                    else if (a === 'aufgabe') showFeedback('Aufgabe erstellt');
                    else showFeedback('Empfehlung ignoriert');
                  }}
                >
                  {naechstesRenewal !== null && naechstesRenewal <= 30 ? (
                    <>
                      Eine Police läuft in <strong>{naechstesRenewal} Tagen</strong> aus.
                      Frühzeitige Markt-Sondierung kann die Prämie spürbar senken.
                    </>
                  ) : (
                    <>
                      Keine akute Erneuerung. Prüfe das Portfolio auf Deckungslücken vor dem
                      Jahresgespräch.
                    </>
                  )}
                </InsightCard>

                <InsightCard
                  title="KI-Hinweis · Cross-Selling"
                  konfidenz={64}
                  actions={[
                    {
                      action: 'aufgabe',
                      label: 'Aufgabe erstellen',
                      icon: ClipboardList,
                      variant: 'outline',
                    },
                    {
                      action: 'ignorieren',
                      label: 'Verwerfen',
                      icon: AlertTriangle,
                      variant: 'ghost',
                      pushEnd: true,
                    },
                  ]}
                  onAction={(a) =>
                    showFeedback(a === 'aufgabe' ? 'Aufgabe erstellt' : 'Empfehlung verworfen')
                  }
                >
                  {kunde.branche.includes('Pharma') || kunde.branche.includes('Chemie') ? (
                    <>
                      Branche mit erhöhtem Cyber-Risiko, aber ohne ausreichende Cyber-Deckung. Eine
                      Offerte könnte gut passen.
                    </>
                  ) : (
                    <>
                      Betriebsunterbruch-Deckung wirkt im Vergleich zur Branche niedrig
                      dimensioniert. Empfehlung zur Überprüfung.
                    </>
                  )}
                </InsightCard>
              </div>
            )}

            {/* Bereich 3: the KPI row — removed when the role hides `kunden.uebersicht.kennzahlen`. */}
            {!isHidden('kunden.uebersicht.kennzahlen') && (
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <KpiCard icon={ShieldCheck} label="Policen" value={String(policen.length)} />
                {/* E4: the finer `jahrespraemie` deny hides just this KPI (the coarser
                    `kennzahlen` deny already removed the whole row above). */}
                {!isHidden('kunden.uebersicht.jahrespraemie') && (
                  <KpiCard icon={Coins} label="Jahresprämie" value={chf(kunde.jahrespraemie)} />
                )}
                <KpiCard
                  icon={ReceiptText}
                  label="Offene Rechnungen"
                  value={String(offeneRechnungen)}
                  hint={offeneRechnungen > 0 ? 'Warten auf Zahlung' : 'Alles bezahlt'}
                />
                <KpiCard
                  icon={RefreshCw}
                  label="Nächstes Renewal"
                  value={naechstesRenewal === null ? '-' : `${naechstesRenewal} T`}
                  hint={naechstesRenewal === null ? 'Keine offene Erneuerung' : 'bis zur Erneuerung'}
                />
              </div>
            )}

            {/* #6: the mandate Vorgang lifecycle (versandt / erinnert / überfällig / eingegangen
                / …). Renders only when the customer has a Vorgang; the card returns null otherwise.
                Bereich 3: removed when the role hides `kunden.uebersicht.vorgang`. */}
            {!isHidden('kunden.uebersicht.vorgang') && (
              <KundeVorgangCard
                vorgaenge={vorgaenge}
                pending={pendingDeliveries}
                renderPendingActions={(p) => (
                  <MandateDeliveryActions
                    kind={p.kind}
                    pending={p.delivery}
                    canDispatch={canDispatchMandate}
                    canSend={canSendMandate}
                    onChanged={onMandateDeliveryChanged}
                  />
                )}
              />
            )}

            {/* D2: a Firma can have several e-mail domains. Firma-only (email_domain
                is a firma FK) and live-only (the component returns null in mock mode). */}
            {subjectType === 'firma' && !isHidden('kunden.uebersicht.domains') && (
              <FirmenDomains firmaId={kunde.id} canManage={canWriteCrm} />
            )}

            {/* H2: the company's master data, which the D1 register enrichment fills and
                which had nowhere to be seen until now. Firma-only. D1-14: editable in place
                (pencil) for a caller with crm.write; edits survive the next enrichment. */}
            {subjectType === 'firma' && !isHidden('kunden.uebersicht.stammdaten') && (
              <FirmenStammdaten
                firmaId={kunde.id}
                canEdit={canWriteCrm}
                refreshKey={firmRefreshKey}
                onSaved={onFirmSaved}
                onEnriched={onFirmSaved}
              />
            )}

            {/* G4: the client-side people, with the Hauptansprechperson marked. Firma-only:
                a natural-person customer is their own contact and has no employee list.
                canEdit is crm.write, which is G3 verbatim ("any user who can edit the
                customer, no special role"). */}
            {subjectType === 'firma' && !isHidden('kunden.uebersicht.kontakte') && (
              <Hauptansprechperson firmaId={kunde.id} canEdit={canWriteCrm} />
            )}

            {/* Stefan 2026-10-06: who may SIGN for the firm (register signatories,
                firma_signatory_set), distinct from who we TALK to (Ansprechpersonen above).
                Read-only; the only write is the Handelsregisterauszug upload, which the existing
                doc-extraction worker turns into the winning set. firmRefreshKey makes this card
                refetch after a Firmenstammdaten EDIT (onFirmSaved) AND after a successful manual
                re-enrichment (FirmenStammdaten onEnriched → onFirmSaved bumps the key). */}
            {subjectType === 'firma' && !isHidden('kunden.uebersicht.zeichnungsberechtigte') && (
              <Zeichnungsberechtigte
                firmaId={kunde.id}
                canUpload={canWriteCrm}
                refreshKey={firmRefreshKey}
              />
            )}

            {/* The way into the area «Mandate & Eintragungen» (the registrations of both mandates). */}
            {live && !eintragungenHidden && (subjectType === 'person' || subjectType === 'firma') && (
              <section className="flex items-center justify-between gap-2 rounded-lg border bg-card p-4" data-testid="me-link-card">
                <div className="flex flex-col">
                  <h3 className="text-sm font-semibold">Mandate & Eintragungen</h3>
                  <p className="text-xs text-muted-foreground">{registrationTile?.tiles.label ?? 'Eintragung bei Versicherern'}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setActiveTab('mandate_eintragungen')} data-testid="me-link-open">
                  Öffnen
                </Button>
              </section>
            )}
            {/* The signed Brokermandat's Meldungen an die Versicherer (one row per insurer). */}
            {brokerMandateId !== null && (subjectType === 'person' || subjectType === 'firma') && !meldungHidden && (
              <MandateRegistrationPanel
                mandateId={brokerMandateId}
                subjectType={subjectType}
                subjectId={id}
                terminated={mandateBadges?.broker.state === 'gekuendigt'}
                readOnly={readOnly}
              />
            )}
            {/* P4-S7: the Versicherer-Benachrichtigungen of the customer's Analysemandat (step 24). */}
            {vbMandateId !== null && !meldungHidden && (
              <VersichererBenachrichtigungenPanel mandateId={vbMandateId} canClose={canWriteCrm} readOnly={readOnly} />
            )}
          </div>
        )}

        {/* Mandate & Eintragungen (Brokermandat Kap. 7.7): one row per insurer, both mandates. */}
        {shownTab === 'mandate_eintragungen' && (subjectType === 'person' || subjectType === 'firma') && (
          <MandateEintragungenTab subjectType={subjectType} subjectId={id} onOpenKommunikation={() => setActiveTab('kommunikation')} />
        )}

        {/* Policen — Bereich 3: the list card, removed when `kunden.policen.liste` is hidden. */}
        {shownTab === 'policen' && !isHidden('kunden.policen.liste') && (
          // Clickability rule (dms/spec.md §4.6): no whole-row onRowClick — only
          // the Policen-Nr. cell (policyColumns[0]) navigates.
          <DataTable columns={policyColumns} data={policen} />
        )}

        {/* Risikoprofil (P2 Placement Slice 1) — generelles Profil + Basissparten + Details je Police. */}
        {shownTab === 'risikoprofil' && (subjectType === 'person' || subjectType === 'firma') && (
          <RisikoprofilTab
            subjectType={subjectType}
            subjectId={id}
            canWrite={canWriteRisk}
            isHidden={isHidden}
            onOpenPolicy={(policyId) =>
              // P2 Placement Slice 2: «Details je Police» deep-links straight to the Risiko-&-Deckung tab.
              void navigate({ to: '/policen/$id', params: { id: policyId }, search: { tab: 'risiko' } })
            }
          />
        )}

        {/* Ausschreibungen (P2 Placement Slice 4) — Placements list/pipeline + create.
            Bereich 3: removed when the `kunden.ausschreibungen.liste` Kachel is hidden. */}
        {shownTab === 'ausschreibungen' &&
          !isHidden('kunden.ausschreibungen.liste') &&
          (subjectType === 'person' || subjectType === 'firma') && (
            <AusschreibungenTab
              subjectType={subjectType}
              subjectId={id}
              canWrite={canWritePlacement}
              policen={policen.map((p) => ({ id: p.id, nr: p.nr, versicherer: p.versicherer, igb2b_code: p.igb2bCode ?? null }))}
            />
          )}

        {/* Rechnungen — Bereich 3: removed when `kunden.rechnungen.liste` is hidden. */}
        {shownTab === 'rechnungen' && !isHidden('kunden.rechnungen.liste') && (
          <DataTable columns={rechnungColumns} data={rechnungen} />
        )}

        {/* Kommunikation — Stefan's 4 buckets (Eingang / Ausgang / Gesendet / Freigabe).
            Bereich 3: removed when `kunden.kommunikation.verlauf` is hidden. */}
        {shownTab === 'kommunikation' && !isHidden('kunden.kommunikation.verlauf') && (
          <div className="flex flex-col gap-3">
            <div
              className="flex flex-wrap gap-1"
              role="tablist"
              aria-label="Kommunikation nach Bucket filtern"
            >
              {KOMM_BUCKETS.map((b) => {
                const count =
                  b.value === 'alle'
                    ? komm.length
                    : komm.filter((m) => kommKategorie(m) === b.value).length;
                const active = kommFilter === b.value;
                return (
                  <button
                    key={b.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setKommFilter(b.value)}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                      active
                        ? 'bg-foreground text-background'
                        : 'bg-muted text-muted-foreground hover:bg-muted/70',
                    )}
                  >
                    {b.label}
                    <span className="ml-1 tabular-nums opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
            <ListPanel
              items={
                kommFilter === 'alle' ? komm : komm.filter((m) => kommKategorie(m) === kommFilter)
              }
              empty={
                kommFilter === 'alle'
                  ? `Keine Kommunikation mit ${kunde.name}.`
                  : `Keine Kommunikation im Bucket „${KOMM_KATEGORIE_LABEL[kommFilter]}".`
              }
              render={(m: Komm) => {
                // #7: reduced-tier rendering (mirrors communication-v2 MessageList). The api already
                // strips the body per tier; here the LIST only shows subject/lock STATE.
                //   metadata → subject hidden ("Inhalt nicht freigegeben")
                //   title    → subject visible + a "nur Titel" lock badge (body gated behind Freigabe)
                //   full     → normal (sender / participant / granted Freigabe)
                const subjectHidden = m.tier === 'metadata';
                const titleOnly = m.tier === 'title';
                const restrictedTeam = mailTeamMarkerName(m.restrictedToTeam);
                return (
                  <div key={m.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                    <span
                      className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
                      aria-hidden
                    >
                      {m.richtung === 'ein' ? (
                        <ArrowDownLeft className="size-3.5" />
                      ) : (
                        <ArrowUpRight className="size-3.5" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      {subjectHidden ? (
                        <p className="flex items-center gap-1 truncate italic text-muted-foreground">
                          <Lock className="size-3 shrink-0" /> Inhalt nicht freigegeben
                          {restrictedTeam != null && (
                            <MailTeamBadge team={restrictedTeam} className="shrink-0" />
                          )}
                        </p>
                      ) : (
                        <p className="flex items-center gap-1.5 truncate font-medium text-foreground">
                          <span className="truncate">{m.betreff}</span>
                          {titleOnly && (
                            <Badge variant="neutral" className="shrink-0 gap-1 text-[10px]">
                              <Lock className="size-2.5" /> nur Titel
                            </Badge>
                          )}
                          {restrictedTeam != null && (
                            <MailTeamBadge team={restrictedTeam} className="shrink-0" />
                          )}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {KOMM_KATEGORIE_LABEL[kommKategorie(m)]} · {m.datum}
                      </p>
                    </div>
                    <StatusDot tone={m.status.tone} label={m.status.label} />
                  </div>
                );
              }}
            />
          </div>
        )}

        {/* Struktur — interactive Beziehungsnetz (UI-U8); falls back to the flat list.
            Bereich 3: removed when `kunden.beziehungen.netz` is hidden. */}
        {shownTab === 'struktur' && !isHidden('kunden.beziehungen.netz') && (
          <BeziehungsnetzTab
            subjectId={kunde.id}
            subjectType={
              subjectType === 'person' || subjectType === 'firma' ? subjectType : undefined
            }
            subjectName={kunde.name}
            fallback={
              <div className="divide-y overflow-hidden rounded-lg border bg-card">
                {beziehungen.length === 0 ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    Keine Beziehungen erfasst.
                  </div>
                ) : (
                  beziehungen.map((b) => (
                    <div key={b.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground">
                          <EntityLink
                            onActivate={() => {
                              if (b.relatedTyp === 'versicherer') {
                                void navigate({ to: `/versicherer/${b.relatedId}` as never });
                              } else {
                                void navigate({
                                  to: '/customers/$id',
                                  params: { id: b.relatedId },
                                });
                              }
                            }}
                          >
                            {b.relatedName}
                          </EntityLink>
                        </p>
                        <p className="text-xs text-muted-foreground">{b.typ}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">{b.rolle}</span>
                    </div>
                  ))
                )}
              </div>
            }
          />
        )}

        {/* Dokumente — DMS M12 (dms/spec.md §4.2): active/latest-version-only,
            GET /documents?client_id=; rows not clickable except the Dokument
            column, which opens the M11 inline viewer (/ablage/$id).
            Bereich 3: removed when `kunden.dokumente.liste` is hidden. */}
        {shownTab === 'dokumente' && !isHidden('kunden.dokumente.liste') && (
          <ClientDocumentsTab clientId={id} />
        )}

        {/* Aktivität — Bereich 3: removed when `kunden.aktivitaet.verlauf` is hidden. */}
        {shownTab === 'aktivitaet' && !isHidden('kunden.aktivitaet.verlauf') && (
          <Timeline events={historie} empty="Keine Ereignisse." />
        )}

        {/* Schäden (stub) */}
        {shownTab === 'schaeden' && (
          <ListPanel
            items={schaeden}
            empty="Keine Schadenfälle erfasst."
            render={(sch) => (
              <div key={sch.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <span
                  className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
                  aria-hidden
                >
                  <ShieldAlert className="size-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{sch.fall}</p>
                  <p className="text-xs text-muted-foreground">
                    {sch.datum} · Reserve {chf(sch.reserve)}
                  </p>
                </div>
                <StatusDot tone={sch.status.tone} label={sch.status.label} />
              </div>
            )}
          />
        )}

        {/* Aufgaben (stub) */}
        {shownTab === 'aufgaben' && (
          <ListPanel
            items={aufgaben}
            empty="Keine offenen Aufgaben."
            render={(a) => (
              <div key={a.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <span
                  className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
                  aria-hidden
                >
                  <ListChecks className="size-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{a.titel}</p>
                  <p className="text-xs text-muted-foreground">
                    <FaelligText restTage={a.faelligTage} showIcon={false} />
                  </p>
                </div>
                <Badge variant={a.prioritaet.tone}>{a.prioritaet.label}</Badge>
              </div>
            )}
          />
        )}

        {/* Zugriff — W3 Managed-Access: give a colleague access to this client's comms. */}
        {shownTab === 'zugriff' &&
          (subjectType === 'person' || subjectType === 'firma' ? (
            <ZugriffFreigabe
              subjectType={subjectType}
              subjectId={kunde.id}
              subjectName={kunde.name}
              onFeedback={showFeedback}
              onError={showFeedback}
            />
          ) : (
            <div className="rounded-lg border border-dashed bg-card p-6 text-center text-sm text-muted-foreground">
              Zugriffs-Freigaben sind für diesen Eintrag nicht verfügbar.
            </div>
          ))}

        {/* Compliance (overflow stub) */}
        {shownTab === 'compliance' && (
          <ListPanel
            items={compliance}
            empty="Keine Compliance-Punkte hinterlegt."
            render={(c) => (
              <div key={c.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <span
                  className={`flex size-7 shrink-0 items-center justify-center rounded-md ${c.ok ? 'bg-sem-ok-bg text-sem-ok' : 'bg-sem-decide-bg text-sem-decide'}`}
                  aria-hidden
                >
                  {c.ok ? (
                    <CheckCircle2 className="size-3.5" />
                  ) : (
                    <AlertTriangle className="size-3.5" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{c.label}</p>
                  <p className="text-xs text-muted-foreground">{c.hinweis}</p>
                </div>
                <StatusDot tone={c.ok ? 'ok' : 'decide'} label={c.ok ? 'Erfüllt' : 'Offen'} />
              </div>
            )}
          />
        )}
      </DetailShell>

      {terminationOpen && (
        <MandatAufloesenDialog
          subjectType={kunde.subjectType}
          subjectId={kunde.id}
          onClose={() => setTerminationOpen(false)}
          onDone={() => setTerminationOpen(false)}
        />
      )}

      {wizardTarget !== null && (
        <MandateWizardDialog
          subjectType={kunde.subjectType}
          subjectId={kunde.id}
          targetStatus={wizardTarget}
          subjectName={kunde.name}
          // Cancel: drop the optimistic status. Nothing was written, so nothing is undone.
          onCancel={() => setWizardTarget(null)}
          // Sent: the api has now written the status WITH the "in Verarbeitung" overlay,
          // so close and let the refetched server value take over from the optimistic one.
          onSent={(result) => {
            setWizardTarget(null);
            // Keep the freshly created mandate id so the insurer-notification section can act
            // on it once the customer has signed (the trigger 404s until then, and says so).
            setMandateId(result.mandateId);
            setMandateKind(result.mandateKind);
            refreshMandateState();
          }}
          // A released or sent mandate of this kind blocks the prepare: close and show it.
          onOpenMandate={(openId) => {
            setWizardTarget(null);
            showMandate(openId);
          }}
          // A Register-Befund was recorded or its Vorgang aborted: the header flag and the tasks follow.
          // The lock notice links to this customer page: just close the wizard.
          onOpenCustomer={() => setWizardTarget(null)}
          onLockChanged={() => {
            void qc.invalidateQueries({ queryKey: qk.registerBefund(kunde.subjectType, kunde.id) });
            void qc.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}

      {/* P4-S7 steps 21-22: the checkbox dialog over the customer's declared insurances. */}
      <Dialog open={vbOpen} onClose={() => setVbOpen(false)} title="Versicherer benachrichtigen" wide>
        {vbOpen && (
          <VersichererBenachrichtigenDialog
            subjectType={kunde.subjectType}
            subjectId={kunde.id}
            onSent={(res) => setVbSentMandateId(res.mandate_id)}
          />
        )}
      </Dialog>

      {/* docs-fix item 1: "Neue Police" — upload a policy PDF for THIS Kunde (holder prefilled). */}
      <UploadPolicyDialog
        open={uploadPoliceOpen}
        onOpenChange={setUploadPoliceOpen}
        prefilledHolder={{ id: kunde.id, name: kunde.name }}
        onUpload={handleUploadPolicy}
      />

      {/* D1-14: the "Bearbeiten" edit dialog (firma only). Reuses the shared edit form; on save
          it refreshes the card + header. */}
      {firmEditOpen && subjectType === 'firma' && (
        <FirmEditDialog
          firmaId={kunde.id}
          onClose={() => setFirmEditOpen(false)}
          onSaved={() => onFirmSaved()}
        />
      )}

      {/* K4: soft-delete confirm. Destructive, irreversible-from-the-UI framing. On confirm →
          DELETE /customers/:id (the hook invalidates the Kunden grid), then back to the list. */}
      <Dialog
        open={deleteOpen}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteOpen(false);
        }}
        title="Kunde löschen"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                deleteMutation.mutate(kunde.id, {
                  onSuccess: () => {
                    setDeleteOpen(false);
                    void navigate({ to: '/customers' });
                  },
                });
              }}
            >
              {deleteMutation.isPending ? 'Wird gelöscht …' : 'Löschen'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Kunde wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        {deleteMutation.isError && (
          <p className="mt-3 text-sm text-status-danger">
            {deleteMutation.error?.message || 'Kunde konnte nicht gelöscht werden.'}
          </p>
        )}
      </Dialog>
    </div>
  );
}
